import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Loader2, Search } from "lucide-react";

import { api } from "@/api/client";
import { useAuth } from "@/context/AuthContext";

type Hit = { id: string; tenant_id?: string; tenant_name?: string | null; label: string; sub?: string | null; badges?: string[] };
type Group = { type: "people" | "employees" | "groups" | "programs" | "rooms" | "leads"; items: Hit[] };
type Row = { type: Group["type"]; hit: Hit; href: string | null };

const PLATFORM_TAB: Partial<Record<Group["type"], string>> = {
    people: "people", groups: "groups", programs: "programs", rooms: "rooms", leads: "leads",
};

function tenantHref(type: Group["type"], h: Hit): string {
    switch (type) {
        case "people": return `/people/${h.id}`;
        case "employees": return `/hr/employees/${h.id}`;
        case "groups": return `/groups/${h.id}`;
        case "programs": return "/programs";
        case "rooms": return "/settings/rooms";
        case "leads": return `/crm?lead=${h.id}`;
    }
}

function platformHref(type: Group["type"], h: Hit): string | null {
    const tab = PLATFORM_TAB[type];
    if (!tab) return null;
    const p = new URLSearchParams({ tab, q: h.label });
    if (h.tenant_id) p.set("tenant_id", h.tenant_id);
    return `/platform?${p.toString()}`;
}

function useSearch(query: string) {
    const [groups, setGroups] = useState<Group[]>([]);
    const [loading, setLoading] = useState(false);
    const [failed, setFailed] = useState(false);
    const seq = useRef(0);
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) { setGroups([]); setLoading(false); setFailed(false); return; }
        const my = ++seq.current;
        setLoading(true);
        const h = setTimeout(() => {
            api.get("/search", { params: { q } })
                .then(r => { if (my === seq.current) { setGroups(r.data.groups || []); setFailed(false); } })
                .catch(() => { if (my === seq.current) { setGroups([]); setFailed(true); } })
                .finally(() => { if (my === seq.current) setLoading(false); });
        }, 300);
        return () => clearTimeout(h);
    }, [query]);
    return { groups, loading, failed };
}

export function GlobalSearch() {
    const { t } = useTranslation();
    const nav = useNavigate();
    const { me, activeTenantId } = useAuth();
    const platformMode = !!me?.user?.is_platform_owner && !activeTenantId;
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const boxRef = useRef<HTMLDivElement>(null);
    const { groups, loading, failed } = useSearch(query);

    const rows: Row[] = useMemo(() => groups.flatMap(g => g.items.map(hit => ({
        type: g.type, hit, href: platformMode ? platformHref(g.type, hit) : tenantHref(g.type, hit),
    }))), [groups, platformMode]);
    const clickable = rows.filter(r => r.href);

    useEffect(() => { setActive(0); }, [groups]);
    useEffect(() => {
        const h = (e: MouseEvent) => { if (!boxRef.current?.contains(e.target as Node)) setOpen(false); };
        window.addEventListener("mousedown", h);
        return () => window.removeEventListener("mousedown", h);
    }, []);

    const go = (r: Row) => { if (!r.href) return; setOpen(false); setQuery(""); nav(r.href); };
    const onKey = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") { setOpen(false); return; }
        if (!clickable.length) return;
        if (e.key === "ArrowDown") { e.preventDefault(); setOpen(true); setActive(i => (i + 1) % clickable.length); }
        else if (e.key === "ArrowUp") { e.preventDefault(); setActive(i => (i - 1 + clickable.length) % clickable.length); }
        else if (e.key === "Enter") { e.preventDefault(); go(clickable[active]); }
    };

    const short = query.trim().length < 2;
    return (
        <div ref={boxRef} className="relative flex-1 max-w-xl" data-testid="global-search">
            <label className="flex items-center gap-2 rounded-md px-3 border transition-colors focus-within:border-[var(--primary)]"
                style={{ background: "var(--input)", borderColor: "var(--border)", height: 40 }}>
                {loading ? <Loader2 size={16} className="animate-spin" style={{ color: "var(--text-muted)" }} /> : <Search size={16} style={{ color: "var(--text-muted)" }} />}
                <input data-testid="global-search-input" type="text" value={query}
                    onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)} onKeyDown={onKey}
                    placeholder={t("search.placeholder") as string} aria-label={t("search.placeholder") as string}
                    className="bg-transparent outline-none text-sm w-full" style={{ color: "var(--text)" }} />
            </label>
            {open && (
                <div className="absolute left-0 right-0 top-11 z-40 rounded-md border shadow-xl max-h-[70vh] overflow-y-auto py-1"
                    style={{ background: "var(--menu)", borderColor: "var(--border)" }} data-testid="global-search-dropdown">
                    {short ? <Note testid="global-search-hint">{t("search.min_chars")}</Note>
                        : failed ? <Note testid="global-search-error" error>{t("search.error")}</Note>
                        : loading && !groups.length ? <Note testid="global-search-loading">{t("search.loading")}</Note>
                        : !groups.length ? <Note testid="global-search-empty">{t("search.empty")}</Note>
                        : groups.map(g => (
                            <div key={g.type} data-testid={`global-search-group-${g.type}`}>
                                <div className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>{t(`search.type.${g.type}`)}</div>
                                {rows.filter(r => r.type === g.type).map(r => (
                                    <ResultRow key={r.hit.id} row={r} active={clickable[active] === r} onPick={() => go(r)} showTenant={!!me?.user?.is_platform_owner} />
                                ))}
                            </div>
                        ))}
                </div>
            )}
        </div>
    );
}

function Note({ children, testid, error }: { children: React.ReactNode; testid: string; error?: boolean }) {
    return <div className="px-3 py-3 text-sm" data-testid={testid} style={{ color: error ? "var(--error-fg)" : "var(--text-muted)" }}>{children}</div>;
}

function ResultRow({ row, active, onPick, showTenant }: { row: Row; active: boolean; onPick: () => void; showTenant: boolean }) {
    const { t } = useTranslation();
    const { hit, href } = row;
    return (
        <button type="button" data-testid={`global-search-result-${row.type}-${hit.id}`} disabled={!href} onClick={onPick}
            onMouseDown={(e) => e.preventDefault()}
            title={href ? undefined : t("search.not_clickable") as string}
            className="w-full text-left px-3 py-2 flex items-center gap-2 text-sm transition-colors disabled:cursor-default"
            style={{ background: active ? "var(--selected-bg)" : "transparent", color: "var(--text)", opacity: href ? 1 : 0.7 }}>
            <span className="flex-1 min-w-0">
                <span className="block truncate">{hit.label}</span>
                {(hit.sub || (showTenant && hit.tenant_name)) && (
                    <span className="block text-xs truncate" style={{ color: "var(--text-muted)" }}>
                        {[showTenant ? hit.tenant_name : null, hit.sub].filter(Boolean).join(" · ")}
                    </span>
                )}
            </span>
            {(hit.badges || []).map(b => <span key={b} className="status-pill status-info shrink-0">{t(`people.type.${b}`, b)}</span>)}
        </button>
    );
}
